import { ArrowUpIcon, CameraIcon, CloseIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import { Button } from '@components/Button'
import { FileInput } from '@components/Field'
import React, { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { AttachedChatCard, ChatCardIcon, ChatGlyph } from './ChatCard'
import {
	AttachedPhoto,
	Attachments,
	CameraButton,
	Chip,
	ChipRow,
	ComposerField,
	ComposerPanel,
	FieldMirror,
	FieldRow,
	FieldWrap,
	LinkPreview,
	LinkPreviewSite,
	LinkPreviewText,
	LinkPreviewTitle,
	MirrorMention,
	RemovePhotoButton,
	ReplyBar,
	ReplyBox,
	ReplySnippet,
	ReplyText,
	ReplyTitle,
	SendButton,
} from './ChatComposer.styles'
import ChatTagPicker from './ChatTagPicker'
import {
	CHAT_MAX_MENTIONS,
	CHAT_MAX_PHOTOS,
	CHAT_MAX_TAGS,
	CHIP_TABS,
	addPhotos,
	attachCard,
	attachedCards,
	canTagMore,
	cancelReply,
	chipTitle,
	hostOf,
	mentionRanges,
	outgoingOf,
	presentQuote,
	previewedLink,
	removeCard,
	removeLinkPreview,
	removePhoto,
	replaceText,
	replyTo,
	reportedEdit,
	syncText,
	tagMember,
	textSegments,
	emptyDraft,
	type ChatCard,
	type ChatQuote,
	type ChatTagPickerTab,
	type ComposerDraft,
	type OutgoingMessage,
} from './core'
import { readDraft, writeDraft } from './draftStore'
import { preparePhoto } from './preparePhoto'
import { useComposerLinkPreview } from './useComposerLinkPreview'

export const COMPOSER_PLACEHOLDER = 'Message, or @ a flight, hotel…'

/** What the Chat asks of its composer. */
export interface ChatComposerHandle {
	/** Shows the quote over the field and puts the cursor in it. */
	reply: (quote: ChatQuote) => void
	/** Adds a resource's card to the Message being written, as "Chat about this" does. */
	attach: (card: ChatCard) => void
	focus: () => void
}

interface ChatComposerProps {
	tripId: string
	myUserId: number
	onSend: (message: OutgoingMessage) => void
}

const tagLimit = () =>
	toast.error(`That's the limit: a message can tag up to ${CHAT_MAX_TAGS} things, and name people up to ${CHAT_MAX_MENTIONS} times.`)

/** Touch keyboards keep Return for a new line; elsewhere Return sends and Shift+Return breaks the line. */
const returnSends = () => !window.matchMedia?.('(hover: none)').matches

/**
 * Where a Message is written, on a panel of its own under the stream: the field, the
 * cards and photos attached to it in one row above, the preview of its first link, the
 * Message it replies to above those, and the chip row. A chip, or typing `@` at the start
 * of a word, opens the picker; the camera adds photos. The Message itself is the pure
 * `ComposerDraft`, kept per trip while the tab lives; this keeps the field and the draft
 * in step. Ported from iOS `ChatComposerView`.
 */
const ChatComposer = forwardRef<ChatComposerHandle, ChatComposerProps>(({ tripId, myUserId, onSend }, ref) => {
	const [draft, setDraftState] = useState<ComposerDraft>(() => readDraft(tripId))
	const draftRef = useRef(draft)
	const fieldRef = useRef<HTMLTextAreaElement>(null)
	const mirrorRef = useRef<HTMLDivElement>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)
	/** Where the field's selection was before the edit the next change makes. */
	const selectionBefore = useRef(0)
	/** Where the caret goes once the field shows a draft the composer changed itself. */
	const pendingCaret = useRef<number | null>(null)
	/** The `@` typed to open the picker, which a pick replaces. */
	const pickerTrigger = useRef<{ offset: number; length: number } | null>(null)
	const [pickerTab, setPickerTab] = useState<ChatTagPickerTab | null>(null)

	const setDraft = useCallback(
		(next: ComposerDraft) => {
			draftRef.current = next
			writeDraft(tripId, next)
			setDraftState(next)
		},
		[tripId],
	)

	const focus = useCallback(() => fieldRef.current?.focus(), [])

	/** Puts the draft's text in the field with the cursor at `caret`. */
	const placeCaret = (caret: number) => {
		pendingCaret.current = caret
	}

	useLayoutEffect(() => {
		const field = fieldRef.current
		if (!field) return
		if (mirrorRef.current) mirrorRef.current.scrollTop = field.scrollTop
		if (pendingCaret.current !== null) {
			const caret = Math.min(pendingCaret.current, draft.text.length)
			pendingCaret.current = null
			field.focus()
			field.setSelectionRange(caret, caret)
			selectionBefore.current = caret
		}
	}, [draft.text])

	const tag = useCallback(
		(card: ChatCard) => {
			const trigger = pickerTrigger.current
			pickerTrigger.current = null
			const current = draftRef.current
			if (card.kind === 'Member') {
				const [person] = card.people
				if (!person) return
				const field = fieldRef.current
				const range = trigger ?? {
					offset: field?.selectionStart ?? current.text.length,
					length: (field?.selectionEnd ?? current.text.length) - (field?.selectionStart ?? current.text.length),
				}
				const tagged = tagMember(current, person, range)
				if (!tagged) {
					tagLimit()
					return
				}
				setDraft(tagged.draft)
				placeCaret(tagged.cursor)
				return
			}
			let next = attachCard(current, card)
			if (!next) {
				tagLimit()
				return
			}
			// The `@` asked for the picker; it isn't part of the Message.
			if (trigger && next.text.slice(trigger.offset, trigger.offset + 1) === '@') {
				next = replaceText(next, { ...trigger, text: '' })
				placeCaret(trigger.offset)
			}
			setDraft(next)
		},
		[setDraft],
	)

	useImperativeHandle(
		ref,
		() => ({
			reply: quote => {
				setDraft(replyTo(draftRef.current, quote))
				focus()
			},
			attach: card => {
				pickerTrigger.current = null
				tag(card)
			},
			focus,
		}),
		[focus, setDraft, tag],
	)

	const openPicker = (tab: ChatTagPickerTab, trigger: { offset: number; length: number } | null) => {
		if (!canTagMore(draftRef.current)) {
			tagLimit()
			return
		}
		pickerTrigger.current = trigger
		setPickerTab(tab)
	}

	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const field = e.target
		const before = draftRef.current
		const caret = field.selectionEnd
		const edit = reportedEdit(before.text, selectionBefore.current, field.value, caret)
		const next = syncText(before, field.value, edit)
		setDraft(next)
		selectionBefore.current = caret
		if (next.text !== field.value) placeCaret(Math.min(caret, next.text.length))
		// An `@` starting a word opens the picker on All, with search focused.
		const typedAt = edit.text === '@' && edit.length === 0
		if (typedAt && (edit.offset === 0 || /\s/.test(before.text.charAt(edit.offset - 1))))
			openPicker('all', { offset: edit.offset, length: 1 })
	}

	const trackSelection = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
		selectionBefore.current = e.currentTarget.selectionStart
	}

	const send = () => {
		const outgoing = outgoingOf(draftRef.current)
		if (!outgoing) return
		onSend(outgoing)
		setDraft(emptyDraft())
		placeCaret(0)
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		trackSelection(e)
		if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && returnSends()) {
			e.preventDefault()
			send()
		}
	}

	const pickPhotos = () => {
		const room = CHAT_MAX_PHOTOS - draftRef.current.photos.length
		if (room <= 0) {
			toast.error(`That's the limit: a message can have up to ${CHAT_MAX_PHOTOS} photos.`)
			return
		}
		fileInputRef.current?.click()
	}

	const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files ?? [])
		e.target.value = ''
		if (files.length === 0) return
		const prepared = await Promise.all(files.map(preparePhoto))
		const photos = prepared.filter((photo): photo is NonNullable<typeof photo> => photo !== null)
		if (photos.length < files.length) toast.error("Some photos couldn't be added. Please try again.")
		const { draft: next, didFit } = addPhotos(draftRef.current, photos)
		photos.slice(next.photos.length - draftRef.current.photos.length).forEach(photo => URL.revokeObjectURL(photo.url))
		if (!didFit) toast.error(`That's the limit: a message can have up to ${CHAT_MAX_PHOTOS} photos.`)
		setDraft(next)
		focus()
	}

	const link = previewedLink(draft)
	const { data: preview } = useComposerLinkPreview(tripId, link)
	const shownPreview = preview && preview.url === link ? preview : null
	const quote = draft.quote ? presentQuote(draft.quote, myUserId) : null
	const cards = attachedCards(draft)
	const canSend = outgoingOf(draft) !== null
	// A trailing line break, or nothing at all, still takes a line, as it does in the field.
	const mirrorText = draft.text === '' || draft.text.endsWith('\n') ? `${draft.text} ` : draft.text

	return (
		<ComposerPanel>
			{quote && (
				<ReplyBox>
					<ReplyBar $quotesMine={quote.isMine} />
					<ReplyText>
						<ReplyTitle $quotesMine={quote.isMine}>Replying to {quote.authorName}</ReplyTitle>
						<ReplySnippet $removed={quote.isRemoved}>
							{quote.glyph && <ChatGlyph glyph={quote.glyph} category={quote.category} fontSize={iconSizes.xs} />}{' '}
							{quote.snippet}
						</ReplySnippet>
					</ReplyText>
					<Button type="button" variant="icon" onClick={() => setDraft(cancelReply(draftRef.current))} aria-label="Cancel reply">
						<CloseIcon sx={{ fontSize: iconSizes.sm }} />
					</Button>
				</ReplyBox>
			)}
			{(cards.length > 0 || draft.photos.length > 0) && (
				<Attachments>
					{cards.map(card => (
						<AttachedChatCard
							key={`${card.kind}-${card.id}`}
							card={card}
							onRemove={removed => setDraft(removeCard(draftRef.current, removed))}
						/>
					))}
					{draft.photos.map((photo, index) => (
						<AttachedPhoto key={photo.id}>
							<img src={photo.url} alt={`Photo ${index + 1}`} />
							<RemovePhotoButton
								type="button"
								aria-label={`Remove photo ${index + 1}`}
								onClick={() => {
									URL.revokeObjectURL(photo.url)
									setDraft(removePhoto(draftRef.current, photo.id))
								}}
							>
								<CloseIcon sx={{ fontSize: iconSizes.xs }} />
							</RemovePhotoButton>
						</AttachedPhoto>
					))}
				</Attachments>
			)}
			{link && (
				<LinkPreview>
					<ChatCardIcon glyph="link" thumbnail={shownPreview?.image} size={48} />
					<LinkPreviewText>
						<LinkPreviewTitle>{shownPreview?.title ?? hostOf(link) ?? link}</LinkPreviewTitle>
						<LinkPreviewSite>{shownPreview?.siteName ?? hostOf(link)}</LinkPreviewSite>
					</LinkPreviewText>
					<Button
						type="button"
						variant="icon"
						onClick={() => setDraft(removeLinkPreview(draftRef.current))}
						aria-label="Remove link preview"
					>
						<CloseIcon sx={{ fontSize: iconSizes.sm }} />
					</Button>
				</LinkPreview>
			)}
			<ChipRow>
				<CameraButton type="button" onClick={pickPhotos} aria-label="Add photos" title="Add photos">
					<CameraIcon sx={{ fontSize: iconSizes.lg }} />
				</CameraButton>
				{CHIP_TABS.map(tab => (
					<Chip key={tab} type="button" onClick={() => openPicker(tab, null)}>
						{chipTitle(tab)}
					</Chip>
				))}
			</ChipRow>
			<FieldRow>
				<FieldWrap>
					<FieldMirror ref={mirrorRef} aria-hidden>
						{textSegments(mirrorText, mentionRanges(draft)).map((segment, index) =>
							segment.type === 'mention' ? (
								<MirrorMention key={index}>{segment.text}</MirrorMention>
							) : (
								<React.Fragment key={index}>{segment.text}</React.Fragment>
							),
						)}
					</FieldMirror>
					<ComposerField
						ref={fieldRef}
						rows={1}
						value={draft.text}
						placeholder={COMPOSER_PLACEHOLDER}
						aria-label="Message"
						onChange={handleChange}
						onKeyDown={handleKeyDown}
						onSelect={trackSelection}
						onMouseUp={trackSelection}
						onScroll={e => {
							if (mirrorRef.current) mirrorRef.current.scrollTop = e.currentTarget.scrollTop
						}}
					/>
				</FieldWrap>
				{canSend && (
					<SendButton type="button" onClick={send} aria-label="Send">
						<ArrowUpIcon sx={{ fontSize: iconSizes.lg }} />
					</SendButton>
				)}
			</FieldRow>
			<FileInput ref={fileInputRef} accept="image/*" multiple onChange={handleFiles} aria-label="Photos to add" />
			<ChatTagPicker
				tripId={tripId}
				myUserId={myUserId}
				openOn={pickerTab}
				tagged={draft.tags}
				onPick={card => {
					setPickerTab(null)
					tag(card)
					focus()
				}}
				onClose={() => {
					// Closed without a pick: a typed `@` stays as text.
					pickerTrigger.current = null
					setPickerTab(null)
					focus()
				}}
			/>
		</ComposerPanel>
	)
})

ChatComposer.displayName = 'ChatComposer'

export default ChatComposer
